// vuex
import store from '@/store';

// vue router
import router from '@/router';
import moment from 'moment';

export default {
  data() {
    return {
      validation: {
        required: [(v) => !!v || 'Required'],
        email: [(v) => !!v || 'Email is required', (v) => /.+@.+\..+/.test(v) || 'Type a valid email address'],
        quantityRules: {
          min(value, min) {
            return value >= min || `Minimum Qty is ${min}`;
          },
          max(value, max) {
            return value <= max || `Maximum Qty is ${max}`;
          }
        },
        length10: [(v) => !v || v.length <= 10 || 'Must be less than 10 characters'],
        length50: [(v) => !v || v.length <= 50 || 'Must be less than 50 characters'],
        length150: [(v) => !v || v.length <= 150 || 'Must be less than 150 characters'],
        length250: [(v) => !v || v.length <= 250 || 'Must be less than 250 characters'],
        length25: [(v) => !v || v.length <= 25 || 'Must be less than 25 characters'],
        length1000: [(v) => !v || v.length <= 1000 || 'Must be less than 1000 characters'],
        numeric: [(v) => !v || !isNaN(v.replace(/,/g, '')) || 'Must be numeric'],
        selection: [(v) => !!v || 'Required']
      }
    };
  },
  computed: {
    // #region : Authentication :
    passwordMatchRule() {
      return () => this.password === this.password2 || 'Passwords do not match';
    },
    passwordRule() {
      return [
        (v) => !!v || 'Password is required',
        (v) =>
          /^(?=.*\d)(?=.*[A-Z]).{8,}$/.test(v) ||
          'Password must contain at least one digit, one uppercase letter, and be at least 8 characters long'
      ];
    },
    // #endregion
    //#region : Product Helpers :
    /**
     * Checks if the product is not fully configured.
     * @returns {boolean} True if the product is not fully configured, false otherwise.
     */
    productNotConfigured() {
      return (
        this.selectedProduct &&
        this.selectedProduct.productSettingModels &&
        this.selectedProduct.productSettingModels.find((x) => x.value == null) != null
      );
    },
    /**
     * Checks if any accessory is not configured.
     * @returns {boolean} True if any accessory is not configured, false otherwise.
     */
    anySelectedAccessoryNotConfigured() {
      return this.allSelectedAccessorySettings.filter((setting) => setting.value == null).length > 0;
    },
    /**
     * Checks if there are any products available in the current subsystem step component.
     * @returns {boolean} True if there are products available, false otherwise.
     */
    hasProducts() {
      return (
        store.state.configurator.currentSubsystemStepComponent &&
        store.state.configurator.currentSubsystemStepComponent.productTypeId
      );
    },
    /**
     * Checks if the current subsystem step component has any settings.
     * @returns {boolean} True if there are settings, false otherwise.
     */
    hasSettings() {
      return (
        store.state.configurator.currentSubsystemStepComponent &&
        store.state.configurator.currentSubsystemStepComponent.componentSettingModels &&
        store.state.configurator.currentSubsystemStepComponent.componentSettingModels.length > 0
      );
    },
    /**
     * Checks if the current step only contains settings.
     * @returns {boolean} True if the step only contains settings, false otherwise.
     */
    isSettingsOnlyStep() {
      return this.hasSettings && !this.hasProducts;
    },
    /**
     * Checks if the selected product has any accessories.
     * @returns {boolean} True if the selected product has accessories, false otherwise.
     */
    hasAccessories() {
      return (
        this.selectedProduct &&
        this.selectedProduct.productAccessoryModels &&
        this.selectedProduct.productAccessoryModels.length > 0
      );
    },
    /**
     * Returns an array of selected accessories for the current selected product.
     * @returns {Array} An array of selected accessories.
     */
    selectedAccessories() {
      if (this.selectedProduct && this.selectedProduct.productAccessoryModels) {
        return this.selectedProduct.productAccessoryModels.filter((x) => x.selected && !x.customerSupplied);
      } else {
        return [];
      }
    },
    /**
     * Retrieves the selected accessory settings.
     * @returns {Array} An array of selected accessory settings.
     */
    allSelectedAccessorySettings() {
      if (this.selectedAccessories)
        return this.selectedAccessories.flatMap((accessory) => accessory.productSettingModels);
      else return [];
    },
    /**
     * Checks if a product is selected.
     * @returns {boolean} True if a product is selected, false otherwise.
     */
    hasSelectedProduct() {
      if (!this.hasProducts) return false;
      const selectedProduct = this.configurator.currentSubsystemStepComponent.productModels.find((x) => x.selected);
      return selectedProduct !== undefined;
    },
    /**
     * Returns an array of unselected products.
     *
     * @returns {Array} An array of unselected products.
     */
    unselectedProducts() {
      if (!this.hasProducts) return [];
      return this.configurator.currentSubsystemStepComponent.productModels.filter((x) => !x.selected);
    },
    /**
     * Returns the selected product from the current subsystem step component.
     * @returns {Object|null} The selected product object, or null if no product is selected.
     */
    selectedProduct() {
      if (this.hasProducts)
        return this.configurator.currentSubsystemStepComponent.productModels.find((x) => x.selected === true);
      else return null;
    }
    //#endregion
  },
  methods: {
    /**
     * Close all open dialogs
     */
    closeAllDialogs() {
      // NOTE:  Dialog must have a ref defined to be closed in this method
      for (const ref in this.$refs) {
        if (this.$refs[ref].$refs.dialog) {
          this.$refs[ref].isActive = false;
        }
      }
    },
    /**
     * Returns the icon image for the system.
     *
     * @param {string} systemName The name of the system.
     * @returns {string} The icon image for the system.
     */
    getIconImage(systemName) {
      // test for existence of image
      const defaultImage = require('@/assets/images/defaultIcon.png');
      try {
        return require(`@/assets/images/${systemName.toLowerCase()}Icon.png`);
      } catch (e) {
        return defaultImage;
      }
    },
    //#region : Product Helpers :
    /**
     * Calculates the price range for an entity.
     *
     * @param {Object} entity - The entity for which to calculate the price range.
     * @returns {string|number} - The price range of the entity.
     */
    getPriceRange(entity) {
      if (!entity.minimumSellPrice || !entity.maximumSellPrice) return `$${this.formatPrice(entity.sellPrice)}`;
      else if (entity.minimumSellPrice == entity.maximumSellPrice)
        return `$${this.formatPrice(entity.minimumSellPrice)}`;
      else return `$${Math.round(entity.minimumSellPrice)}-$${Math.round(`${entity.maximumSellPrice}`)}`;
    },
    /**
     * Checks if an product has a price range.
     *
     * @param {object} product - The product to check.
     * @returns {boolean} - Returns true if the product has a price range, false otherwise.
     */
    productHasPriceRange(product) {
      if (product.configuredAccessoryId == null) {
        if (product.minimumSellPrice != null) {
          return true;
        } else {
          return false;
        }
      }
      return false;
    },
    /**
     * Checks if an accessory has a price range.
     *
     * @param {object} accessory - The accessory to check.
     * @returns {boolean} - Returns true if the accessory has a price range, false otherwise.
     */
    accessoryHasPriceRange(accessory) {
      if (accessory.configuredAccessoryId == null) {
        if (accessory.minimumSellPrice != null) {
          return true;
        } else {
          return false;
        }
      }
      return false;
    },
    //#endregion

    //#region : Authentication :
    /**
     * Shows the login dialog
     */
    showLoginDialog() {
      this.closeAllDialogs();
      if (this.$route.name != 'project' && this.$route.name != 'checkout') router.push({ path: 'project' });
      store.commit('app/setShowLoginDialog', true);
    },
    /**
     * Shows the create account dialog
     */
    showCreateAccountDialog() {
      this.closeAllDialogs();
      store.commit('app/setShowCreateAccountDialog', true);
    },
    /**
     * Shows the forgot password dialog
     */
    showForgotPasswordDialog() {
      // to be implemented
      this.closeAllDialogs();
      store.commit('app/setShowForgotPasswordDialog', true);
    },
    //#endregion

    //#region : Sig Handling :
    /**
     * Callback function to navigate to the next step in the configurator.
     */
    nextStepCallback: function () {
      const stepIndex = store.state.configurator.requestedStepIndex;
      if (stepIndex != null) {
        return () => {
          this.goToStep(stepIndex);
        };
      } else {
        return () => {
          this.goToStep(this.getCurrentStepIndex() + 1);
        };
      }
    },
    /**
     * Calls the `navToProject` method to navigate to the project.
     */
    projectCallback: function () {
      if (store.state.configurator.currentProject.id) this.openProject(store.state.configurator.currentProject.id);
      else this.navToProjectPage();
    },
    /**
     * Calls the sig responder callback function.
     * @param {*} sigMessageModel
     */
    sigResponseCallback: function (sigMessageModel) {
      this.hideSig();
      store.dispatch('app/sigRespond', sigMessageModel).then((response) => {
        this.showPlainDialog({
          display: 'Now',
          message: response.message,
          messageTitle: response.status,
          parentId: 0,
          parentType: '',
          published: true
        });
      });
    },
    /**
     * Callback function for the checkout process.
     * It navigates to the checkout page.
     */
    checkoutCallback: function () {
      this.navToCheckoutPage();
    },
    /**
     * Builds an array of buttons based on the provided sigMessageModel.
     * @param {Object} sigMessageModel - The sigMessageModel object.
     * @returns {Array} - An array of buttons.
     */
    buildSigButtonArray(sigMessageModel) {
      if (!sigMessageModel) return [];
      sigMessageModel.buttonArray = [];
      const buttons = sigMessageModel.dialogButtons.split(',');
      buttons.forEach((button) => {
        switch (button.toLowerCase()) {
          case 'ok':
            sigMessageModel.buttonArray.push({
              label: 'Got It!',
              callback: this.hideSig,
              icon: 'mdi-thumb-up-outline',
              color: '#7c40dc'
            });
            break;
          case 'submit':
            sigMessageModel.buttonArray.push({
              label: 'Lets do this!',
              callback: this.sigResponseCallback.bind(null, sigMessageModel),
              icon: 'mdi-check',
              color: 'green'
            });
            break;
          case 'go':
            sigMessageModel.buttonArray.push({
              label: 'Go Anyway!',
              callback: this.nextStepCallback(),
              icon: '',
              color: '#272727'
            });
            break;
          case 'project':
            sigMessageModel.buttonArray.push({
              label: 'View Project',
              callback: this.projectCallback,
              icon: 'mdi-folder-outline',
              color: '#272727'
            });
            break;
          case 'checkout':
            sigMessageModel.buttonArray.push({
              label: 'Checkout',
              callback: this.checkoutCallback,
              icon: 'mdi-folder-outline',
              color: 'green'
            });
            break;
        }
      });
    },
    /**
     * Processes the Sig response.
     *
     * @param {Object} sigMessageModel - The sig message model.
     */
    processSigResponse(sigMessageModel) {
      if (sigMessageModel) {
        if (sigMessageModel.display.toLowerCase() == 'now' && sigMessageModel.published) {
          // add buttonArray if needed
          this.buildSigButtonArray(sigMessageModel);
          this.showSig(0, sigMessageModel, false);
        }
      }
    },
    /**
     * Shows a signature message.
     *
     * @param {string} sigId - The ID of the signature.
     * @param {object} sigMessageModel - The signature message model.
     * @param {boolean} [manageCookie=true] - Indicates whether to manage the cookie.
     */
    showSig(sigId, sigMessageModel, manageCookie = true) {
      const cookie = this.$cookies.get(`sigMessage-${sigId}`);

      if (!cookie || !manageCookie) {
        store.commit('app/setSig', sigMessageModel);
        store.commit('app/showSig', true);
      }
    },
    /**
     * Hides the sig message
     */
    hideSig(sigId, manageCookie = true) {
      if (manageCookie) {
        // save cookie
        this.$cookies.set(`sigMessage-${sigId}`, 'true');
      }

      store.commit('app/showSig', false);
    },
    /**
     * Shows a plain dialog with the given sigMessageModel.
     *
     * @param {Object} sigMessageModel - The sigMessageModel to display in the plain dialog.
     */
    showPlainDialog(sigMessageModel) {
      store.commit('app/setPlainDialog', sigMessageModel);
      store.commit('app/showPlainDialog', true);
    },
    /**
     * Hides the sig message
     */
    hidePlainDialog() {
      store.commit('app/showPlainDialog', false);
    },
    //#endregion

    //#region : Formatting :
    /**
     * Formats a number to x,xxx.xx
     * @param {*} value
     */
    formatPrice(value) {
      let val = (value / 1).toFixed(2);
      return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },
    formatDate(date) {
      return date ? moment(date).format('MMMM DD YYYY') : '';
    },
    /**
     * Formats a string from PascalCase to lowercase with spaces between words.
     *
     * @param {string} value - The string to be formatted.
     * @returns {string} The formatted string.
     */
    formatPascalCase(value) {
      return (
        value
          // Insert a space before all uppercase letters that are preceded by a lowercase letter
          .replace(/([a-z])([A-Z])/g, '$1 $2')
          // Insert a space before all uppercase letters that are preceded by a number
          .replace(/([0-9])([A-Z])/g, '$1 $2')
      );
    },
    //#endregion

    //#region : Delete Entities :
    /**
     * Deletes a subsystem
     * @param {*} id
     */
    deleteSubsystem: function (id) {
      if (id > 0) {
        store.commit('configurator/setCurrentProjectLoading', true);
        store
          .dispatch('configurator/deleteSubsystem', id)
          .then(() => {
            this.openProject(store.state.configurator.currentProject.id);
          })
          .finally(() => {
            store.commit('configurator/setCurrentProjectLoading', false);
          });
      }
    },
    /**
     * Deletes a system
     * @param {*} id
     */
    deleteSystem: function (id) {
      if (id > 0) {
        store.dispatch('configurator/deleteSystem', id).then(() => {
          store.dispatch('configurator/getSystems', {
            projectId: store.state.configurator.currentProject.id
          });
          this.navToProjectPage();
        });
      }
    },
    /**
     * Deletes a project
     * @param {*} id
     */
    deleteProject: function () {
      if (store.state.configurator.currentProject) {
        store.commit('configurator/setCurrentProjectLoading', true);
        store
          .dispatch('configurator/deleteProject', store.state.configurator.currentProject.id)
          .then(() => {
            store.dispatch('configurator/getProjects');
            store.commit('configurator/resetCurrentProject');
          })
          .finally(() => {
            store.commit('configurator/setCurrentProjectLoading', false);
          });
      }
    },
    //#endregion

    //#region : Open Entities :
    /**
     * Returns a full subsystem heirarchy
     * @param {*} subsystemId
     * @returns
     */
    async openSubsystemDetails(subsystemId) {
      return await store.dispatch('configurator/getSubsystemDetails', subsystemId);
    },
    /**
     * Opens project with input ID, or opensnew project dialog if no id is provided
     * @param {*} id
     */
    openProject: function (id, openFirstSubsystem = false, openSystemId = 0, openSubsystemId = 0) {
      this.unwindStack('project');
      if (id > 0) {
        this.navToProjectPage();
        this.$store.commit('configurator/setCurrentProjectLoading', true);
        this.$store.dispatch('configurator/getProject', { projectId: id }).then(() => {
          this.$store.commit('configurator/setCurrentProjectLoading', false);
          this.$store.dispatch('configurator/getSystems', { projectId: id }).then(() => {
            const promises = [];

            store.state.configurator.systems.forEach((x) => {
              promises.push(store.dispatch('configurator/getSubsystems', { systemId: x.id }));
            });

            Promise.all(promises).then(() => {
              // open first subsystem in first system
              if (openFirstSubsystem) {
                // find first system with a subsystem
                let firstNonEmptyArray = null;
                let firstNonEmptyArrayKey = null;

                const subsystems = store.state.configurator.subsystems;

                for (let key in subsystems) {
                  if (
                    Object.prototype.hasOwnProperty.call(subsystems, key) &&
                    Array.isArray(subsystems[key]) &&
                    subsystems[key].length > 0
                  ) {
                    firstNonEmptyArray = subsystems[key];
                    firstNonEmptyArrayKey = key;
                    break; // Exit the loop once we find the first non-empty array
                  }
                }
                if (firstNonEmptyArray) {
                  this.openSubsystem(firstNonEmptyArray[0].systemId, firstNonEmptyArray[0].id, 0);
                }
              } else if (openSystemId > 0 && openSubsystemId > 0) {
                this.openSubsystem(openSystemId, openSubsystemId, 0);
              }
            });
          });
        });
      } else {
        // create new
        store.commit('configurator/showCreateNewProjectDialog', true);
      }
    },
    /**
     * Opens specified subsystem
     */
    openSubsystem: function (systemId, subsystemId, stepIndex) {
      this.navToSubsystemPage();
      store.commit('configurator/setIsLoadingSystem', true);
      // load currentSystem
      store
        .dispatch('configurator/getSystem', systemId)
        .then(() => {
          // load currentSubsystem
          store.commit('configurator/setIsLoadingSubsystem', true);
          this.$store
            .dispatch('configurator/getSubsystem', {
              systemId: systemId,
              subsystemId: subsystemId,
              resetSteps: true
            })
            .then(() => {
              this.navToSubsystemPage();
              this.loadCurrentSubsystemStep(subsystemId, stepIndex);
            })
            .finally(() => {
              store.commit('configurator/setIsLoadingSubsystem', false);
            });
        })
        .finally(() => {
          store.commit('configurator/setIsLoadingSystem', false);
        });
    },
    //#endregion

    //#region : Navigation Helpers :

    /**
     * Removes the specified query parameter from the current route's query object and updates the route.
     * @param {string} param - The name of the query parameter to remove.
     */
    removeQueryParam(param) {
      const query = { ...this.$route.query };
      delete query[param];
      this.$router.push({ query });
    },
    /**
     * Resets the checkout state
     */
    resetCheckout: function () {
      store.commit('checkout/resetCheckout');
    },
    /**
     * Loads a step and updates view
     * @param {*} stepIndex - The index of the step to load.
     */
    goToStep: function (stepIndex) {
      if (
        this.isLastStep(this.getCurrentStepIndex()) &&
        stepIndex >= this.getCurrentStepIndex() &&
        !this.isSettingsOnlyStep &&
        this.getCurrentStepIndex() !== stepIndex
      ) {
        this.finishSubsystem();
      }
      // check for existence of stepIndex, send back to main page if not exists
      else if (!store.state.configurator.currentSubsystemSteps[stepIndex]) {
        this.openProject(store.state.configurator.currentProject.id);
      } else {
        // loads the current step for the loaded subsystem
        this.loadCurrentSubsystemStep(store.state.configurator.currentSubsystem.id, stepIndex);
      }
    },
    /**
     * Goes to a specific step in a given subsystem
     * @param {*} stepIndex
     */
    saveAndGoToStep: function (stepIndex) {
      store.commit('configurator/setRequestedStepIndex', stepIndex);
      const reloadingCurrentStep = this.getCurrentStepIndex() === stepIndex;

      const actionType = reloadingCurrentStep ? 'Validate' : 'ValidateAndNavigate';
      const callback = this.goToStep.bind(this, stepIndex);
      this.saveSubsystemStepProgress('subsystem', actionType, callback, reloadingCurrentStep);
    },
    /**
     * Saves progress on a subsystem step
     */
    saveSubsystemStepProgress(destination, actionType = 'Validate', callback = null, reloadStep = true) {
      // if currently editing a subsystem step, save changes and reload tree
      if (store.state.configurator && store.state.configurator.currentSubsystemStepComponent == null) return;
      if (this.$route.name == 'subsystem' && this.getCurrentStepIndex() > -1) {
        store.commit('configurator/setIsValidatingComponent', true);
        return store.dispatch('configurator/validateComponent', actionType).then((response) => {
          store.commit('configurator/setIsValidatingComponent', false);
          // update sigmessage if provided
          if (
            response.sigMessageModel &&
            response.sigMessageModel.display.toLowerCase() == 'ondemand' &&
            response.sigMessageModel.published
          ) {
            store.commit('configurator/updateCurrentStepComponentSigMessages', response.sigMessageModel.message);
          }
          // navigate to next step if no sig message
          if (response.sigMessageModel && response.sigMessageModel.display.toLowerCase() == 'now') {
            this.processSigResponse(response.sigMessageModel);
          } else {
            if (callback) {
              callback();
            }
          }

          store.dispatch('configurator/refreshTreeSummaryData').then(() => {
            // unwind stack as necessary
            this.unwindStack(destination);

            if (store.state.configurator.currentSubsystem && reloadStep) {
              // reloads the current step for the loaded subsystem
              this.loadCurrentSubsystemStep(store.state.configurator.currentSubsystem.id, this.getCurrentStepIndex());
            }
          });
        });
      }
    },
    /**
     * Finishes the current subsystem
     */
    finishSubsystem: function () {
      store
        .dispatch('configurator/raiseSigEvent', {
          parentType: 'Subsystem',
          parentEvent: 'Finish',
          parentId: store.state.configurator.currentSubsystem.id
        })
        .then((sigMsg) => {
          if (sigMsg) {
            this.processSigResponse(sigMsg);
          }
        });
    },
    /**
     * Clears state entities when navigating back up the tree
     * @param {*} newLevel
     */
    unwindStack: function (newLevel) {
      if (!newLevel) newLevel = '';
      switch (newLevel.toLowerCase()) {
        case 'subsystem': // no unwinding needed
          break;
        case 'project':
          store.commit('configurator/resetCurrentSystem');
          store.commit('configurator/resetCurrentSubsystem', true);
          store.commit('configurator/resetCurrentSubsystemStepComponent');
          store.commit('configurator/resetCurrentSubsystemStep');
          store.commit('configurator/resetCurrentSubsystemSteps');
          break;
        case 'checkout':
          store.commit('configurator/resetCurrentSystem');
          store.commit('configurator/resetCurrentSubsystem', true);
          store.commit('configurator/resetCurrentSubsystemStepComponent');
          store.commit('configurator/resetCurrentSubsystemStep');
          store.commit('configurator/resetCurrentSubsystemSteps');
          break;
        default:
          store.commit('configurator/resetCurrentProject');
          store.commit('configurator/resetCurrentSystem');
          store.commit('configurator/resetCurrentSubsystem', true);
          store.commit('configurator/resetCurrentSubsystemStepComponent');
          store.commit('configurator/resetCurrentSubsystemStep');
          store.commit('configurator/resetCurrentSubsystemSteps');
          break;
      }
    },
    /**
     * Checks if the given step is the current step in the configurator.
     * @param {Object} step - The step to check.
     * @returns {boolean} - True if the given step is the current step, false otherwise.
     */
    isCurrentStep: function (step) {
      if (store.state.configurator.currentSubsystemStepComponent == null) return false;
      return (
        step.componentDefinitionId === store.state.configurator.currentSubsystemStepComponent.componentDefinitionId
      );
    },
    /**
     * Returns the index of the current step in the progress bar.
     * @returns {number} The index of the current step.
     */
    getCurrentStepIndex: function () {
      if (store.state.configurator.currentSubsystemStepComponent == null) return -1;
      return store.state.configurator.currentSubsystemSteps.findIndex((x) =>
        this.isCurrentStep(x, store.state.configurator.currentSubsystemStepComponent)
      );
    },
    /**
     * returns TRUE if the input step is the last step in the current subsystem
     * @param {*} stepIndex
     * @returns
     */
    isLastStep: function (stepIndex) {
      if (store.state.configurator.currentSubsystemStepComponent == null) return false;

      return store.state.configurator.currentSubsystemSteps.length - 1 === stepIndex;
    },
    /**
     * Refreshes the currently loaded subsystem step
     */
    refreshCurrentSubsystemStep(stepIndex) {
      if (!store.state.configurator.currentSubsystem) return;

      store.commit('configurator/setIsLoadingStepComponent', true);
      this.$store
        .dispatch('configurator/getStepComponent', {
          subsystemId: store.state.configurator.currentSubsystem.id,
          stepIndex: stepIndex
        })
        .then((response) => {
          // show step sig message if present
          if (
            store.state.configurator.currentSubsystemStepComponent &&
            store.state.configurator.currentSubsystemStepComponent.sigMessageModel
          ) {
            this.processSigResponse(store.state.configurator.currentSubsystemStepComponent.sigMessageModel);
          }
          //store.commit('configurator/setRequestedStepIndex', null);
        })
        .finally(() => {
          store.commit('configurator/setIsLoadingStepComponent', false);
        });
    },
    /**
     * Returns the default step index based on the current subsystem's steps and status.
     * @returns {number} The default step index.
     */
    getDefaultStepIndex() {
      let defaultStep = 0;
      const steps = store.state.configurator.currentSubsystemSteps;
      if (steps.length > 0) {
        const stepStatusKeys = steps.map((x) => x.status);
        const currentStepIndex = stepStatusKeys.lastIndexOf('Valid');
        if (
          store.state.configurator.currentSubsystem &&
          store.state.configurator.currentSubsystem.percentComplete == 100
        ) {
          defaultStep = 0;
        } else if (currentStepIndex >= -1 && steps.length > currentStepIndex + 1) {
          defaultStep = currentStepIndex + 1;
        } else if (currentStepIndex >= -1 && steps.length == currentStepIndex + 1) {
          defaultStep = currentStepIndex;
        } else {
          defaultStep = 0;
        }
      }
      return defaultStep;
    },
    /**
     * Opens a specific step for a given subsystem and updates the current step id in state
     * @param {*} subsystemId
     * @param {Number}  stepIndex   Array index of current step
     */
    loadCurrentSubsystemStep(subsystemId, stepIndex) {
      if (stepIndex == null) stepIndex = this.getDefaultStepIndex(); // use default step if none provided
      var canNavigate = store.state.configurator.currentSubsystemSteps[stepIndex] != undefined; // check if step exists
      // console.log('canNavigate:' + JSON.stringify(store.state.configurator.currentSubsystemSteps[stepIndex]));
      store.commit('configurator/setIsLoadingSubsystemSteps', true);
      this.$store
        .dispatch('configurator/getSubsystemSteps', {
          subsystemId: subsystemId
        })
        .then(() => {
          // determine default step
          if (!canNavigate) {
            // update current step in state
            this.refreshCurrentSubsystemStep(stepIndex);
          }
        })
        .finally(() => {
          store.commit('configurator/setIsLoadingSubsystemSteps', false);
        });
      // attempt to navigate to step before call returns for better UX
      if (canNavigate) {
        // update current step in state and navigate to step
        this.refreshCurrentSubsystemStep(stepIndex);
      }
    },
    navToProjectStart() {
      this.unwindStack();
      if (this.$route.name != 'project') router.push({ path: 'project' });
    },
    /**
     * Updates all views to Project page
     */
    navToProjectPage() {
      //this.saveSubsystemStepProgress('project');
      if (this.$route.name != 'project') router.push({ path: 'project' });
    },
    /**
     * Moves to Account Page
     */
    navToAccountPage() {
      //this.saveSubsystemStepProgress('account');

      if (this.$route.name != 'account') router.push({ path: 'account' });
    },
    /**
     * Updates all views to Checkout page
     */
    navToCheckoutPage() {
      this.unwindStack('checkout');
      // if (store.state.app.user.isGuest) {
      //   store.commit('app/setShowCreateAccountDialog', true);
      //   return;
      // }
      // ensure a project is loaded to check out
      if (!store.state.configurator.currentProject || !store.state.configurator.currentProject.id) return;

      //this.saveSubsystemStepProgress('checkout');
      store.commit('checkout/setProjectId', store.state.configurator.currentProject.id);
      //#region : create payment intent :
      const request = {
        projectId: store.state.checkout.projectId,
        items: [
          {
            id: '1',
            amount: store.state.configurator.currentProject.sellPrice.toString()
          }
        ]
      };
      // fire checkout discount event
      this.$store
        .dispatch('configurator/raiseSigEvent', {
          parentType: 'Project',
          parentEvent: 'Promotion',
          parentId: store.state.checkout.projectId
        })
        .then((sigMsg) => {
          if (sigMsg) {
            this.processSigResponse(sigMsg);
          }
        });
      store.dispatch('checkout/createPaymentIntent', request).then((data) => {
        store.commit('checkout/setClientSecret', data.clientSecret);
        store.commit('checkout/setPaymentIntentId', data.paymentIntentId);
      });
      //#endregion

      if (this.$route.name != 'checkout') router.push({ path: 'checkout' });
    },
    /**
     * Updates all views to the Subsystem page
     */
    navToSubsystemPage() {
      if (this.$route.name != 'subsystem') router.push({ path: 'subsystem' });
    },
    /**
     * Updates all views to settings page
     */
    navToSettingsPage() {
      //this.saveSubsystemStepProgress('settings');
      if (this.$route.name != 'settings') router.push({ path: 'settings' });
    },
    /**
     * Updates all views to reports page
     */
    navToReportsPage() {
      //this.saveSubsystemStepProgress('reports');
      if (this.$route.name != 'reports') router.push({ path: 'reports' });
    },
    /**
     * Updates all views to order successful page
     */
    navToOrderSuccessful(orderId) {
      if (this.$route.name != 'account') {
        let path = { path: 'account' };
        if (orderId) path.query = { orderId: orderId };
        router.push(path);
      }
    },
    //#endregion

    //#region : Positioning Helpers :
    /**
     * Gets X and Y offsets of an html element for current viewport
     * @param {*} el
     * @returns
     */
    getOffset: function (el) {
      var _x = 0;
      var _y = 0;
      while (el && !isNaN(el.offsetLeft) && !isNaN(el.offsetTop)) {
        _x += el.offsetLeft - el.scrollLeft;
        _y += el.offsetTop - el.scrollTop;
        el = el.offsetParent;
      }
      return { top: _y, left: _x };
    },
    //#endregion

    //#region : Color Helpers :
    /**
     * Returns the color based on the percent complete.
     *
     * @param {number} percentComplete - The percentage of completion.
     * @returns {string} The color code based on the percent complete.
     */
    getProgressColor(percentComplete) {
      if (percentComplete == 100) return '#30C473';
      else if (percentComplete < 40) return '#EB475B';
      else return '#FDB022';
    },
    getRandomPurpleShade: function () {
      // Fix red and blue components to give a purple hue
      const red = 150; // You can adjust this value
      const blue = 200; // You can adjust this value

      // Generate a random green component
      const green = Math.floor(Math.random() * 256);

      // Convert RGB values to hexadecimal
      const hexColor = `#${((1 << 24) | (red << 16) | (green << 8) | blue).toString(16).slice(1)}`;

      return hexColor;
    },
    getRandomPastelColor: function () {
      // Generate random values for red, green, and blue
      const red = Math.floor(Math.random() * 256);
      const green = Math.floor(Math.random() * 256);
      const blue = Math.floor(Math.random() * 256);

      // Calculate the pastel version by averaging with a high value
      const pastelRed = Math.floor((red + 255) / 2);
      const pastelGreen = Math.floor((green + 255) / 2);
      const pastelBlue = Math.floor((blue + 255) / 2);

      // Convert RGB values to hexadecimal
      const hexColor = `#${((1 << 24) | (pastelRed << 16) | (pastelGreen << 8) | pastelBlue).toString(16).slice(1)}`;
      return hexColor;
    }
    //#endregion
  }
};
